import { RestError } from '@azure/core-http';
import { HistoricalUtilisationDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { Paths, ProjectPathParams } from '../../../app/router/app.router';
import { PermissionBoundary } from '../../../core/permissions/permission-boundary.component';
import { WindowTitle } from '../../../core/window-title/window-title.component';
import { ErrorFullPage } from '../../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { ListPageHeader } from '../../../ui/components/pure/page-headers/list/list-page-header.component';
import { LockerUtilisationChartCard } from '../../../ui/components/pure/reports/locker-utilisation/locker-utilisation-chart-card/locker-utilisation-chart-card.component';
import { makeLockerUtilisationChartProps } from './locker-utilisation-props.map';
import { useLockerUtilisationReport } from './locker-utlisation-report.hook';
import dayjs from 'dayjs';
import { Container } from '@mui/material';

export function LockerUtilisationReportPage({ params }: { params: ProjectPathParams<Paths.lockerUtilisationReport> }) {
  const { data, state, handlers, options } = useLockerUtilisationReport(params.projectID);

  const error = data.error;

  if (error) {
    if ((error as RestError).statusCode === 401) {
      return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
    }

    if ((error as RestError).statusCode === 403) {
      return <ErrorFullPage preset="ACCESS_DENIED" />;
    }

    if ((error as RestError).statusCode === 404) {
      return <ErrorFullPage preset="NOT_FOUND" />;
    }

    return <ErrorFullPage />;
  }

  // We want the chart to stay in place when the data is fetching in the background, but we don't want to set the query
  // to "keepPreviousData" because the logic within makeLockerUtilisationChartProps might perform invalid actions on the
  // previous data while its waiting. To solve this, we pass in empty mock data while the real data is undefined

  const mockUtilisationData: HistoricalUtilisationDto = {
    metadata: {
      fromDate: state.utilisationQuery.fromDate ?? dayjs().subtract(1, 'week').toDate(),
      toDate: state.utilisationQuery.toDate,
      groupBy: state.utilisationQuery.groupBy,
      hubID: state.utilisationQuery.hubID,
      projectID: '123'
    },
    groups: []
  };

  const chartProps = makeLockerUtilisationChartProps(data.data || mockUtilisationData, state.range);

  return (
    <Container>
      <PermissionBoundary
        availableUiFeatures={['internal-groundfloor']}
        notify={{
          render() {
            return <ErrorFullPage preset="ACCESS_DENIED" />;
          }
        }}
      >
        <WindowTitle pageTitle="Reports" detail="Legacy Delivery Container Utilisation" />
        <ListPageHeader title="Legacy Delivery Container Utilisation" subtitle="Visualise locker usage over time" />
        <LockerUtilisationChartCard
          ChartProps={{ ...chartProps, isLoading: data.isLoading || data.isFetching }}
          HeaderProps={{
            dateRangeSelect: {
              onChange: handlers.onSelectDateRange,
              options: options.dateRangeOptions,
              value: state.range
            },
            groupingSelect: {
              onChange: handlers.onSelectGroupBy,
              options: options.groupByOptions,
              value: state.utilisationQuery.groupBy
            },
            hubSelect: {
              onChange: handlers.onSelectHub,
              options: options.hubOptions,
              value: state.utilisationQuery.hubID || 'all'
            }
          }}
        />
      </PermissionBoundary>
    </Container>
  );
}
